<template>
  <div class="past-due-overlay" @click.self="$emit('close')">
    <div class="past-due-dialog" role="dialog" aria-labelledby="past-due-title">
      <div class="past-due-header">
        <h3 id="past-due-title">Past due texts</h3>
        <button type="button" class="past-due-close" @click="$emit('close')">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="past-due-body">
        <p class="past-due-hint">
          One row per lease for the current rent cycle. Everyone starts checked.
          Clear a box to skip that lease. A row marked Opt-in first gets the
          reply-START message now, and the past-due amount after they opt in.
        </p>

        <div v-if="loading" class="past-due-loading">
          <i class="fas fa-spinner fa-spin"></i> Loading leases...
        </div>

        <div v-else-if="recipients.length" class="past-due-list">
          <div class="past-due-row past-due-head">
            <span></span>
            <span>Name</span>
            <span>Property</span>
            <button
              type="button"
              class="past-due-amount-sort"
              @click="toggleAmountSort"
            >
              {{ amountSortLabel }}
            </button>
            <span>Opt-in first</span>
          </div>
          <label
            v-for="row in sortedRecipients"
            :key="pastDueRowKey(row)"
            class="past-due-row"
            :class="{ sent: row.alreadySent }"
            @click.prevent="toggleRow(row)"
          >
            <input
              type="checkbox"
              :checked="isChecked(row)"
              :disabled="sending || !!resultText"
            />
            <span>{{ row.name }}</span>
            <span>{{ row.address }}</span>
            <span class="past-due-amount">{{ pastDueAmountLabel(row.amount) }}</span>
            <span class="past-due-optin">{{ pastDueRowNote(row) }}</span>
          </label>
        </div>

        <p class="past-due-status" :class="{ error: !!errorText, success: !!resultText }">
          {{ statusText }}
        </p>
      </div>

      <div class="past-due-footer">
        <button type="button" class="past-due-btn secondary" @click="$emit('close')">
          {{ resultText ? 'Close' : 'Cancel' }}
        </button>
        <button
          type="button"
          class="past-due-btn primary"
          :disabled="!confirmState.canSend || sending || loading || !!resultText"
          @click="sendTexts"
        >
          {{ sending ? 'Sending...' : 'Send texts' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axiosConfig';
import {
  pastDueConfirmState,
  pastDueDefaultSelection,
  pastDueRowKey,
  pastDueSendBody,
  pastDueRowNote,
  pastDueSendResult,
  pastDueToggleSelection,
  pastDueSortByAmount,
  pastDueNextAmountSort,
  pastDueAmountLabel,
} from '@/utils/pastDueTexts';

export default {
  name: 'PastDueSmsDialog',
  emits: ['close'],
  data() {
    return {
      loading: true,
      sending: false,
      recipients: [],
      selectedKeys: [],
      errorText: '',
      resultText: '',
      amountSort: '',
    };
  },
  computed: {
    amountSortLabel() {
      if (this.amountSort === 'asc') return 'Amount ↑';
      if (this.amountSort === 'desc') return 'Amount ↓';
      return 'Amount due';
    },
    sortedRecipients() {
      if (!this.amountSort) return this.recipients;
      return pastDueSortByAmount(this.recipients, this.amountSort);
    },
    confirmState() {
      return pastDueConfirmState({
        recipients: this.recipients,
        selectedKeys: this.selectedKeys,
      });
    },
    statusText() {
      if (this.loading) return '';
      return this.errorText || this.resultText || this.confirmState.prompt;
    },
  },
  mounted() {
    this.loadRecipients();
  },
  methods: {
    pastDueRowKey,
    pastDueRowNote,
    pastDueAmountLabel,
    toggleAmountSort() {
      this.amountSort = pastDueNextAmountSort(this.amountSort);
    },
    isChecked(row) {
      return this.selectedKeys.includes(pastDueRowKey(row));
    },
    toggleRow(row) {
      if (this.sending || this.resultText) return;
      this.selectedKeys = pastDueToggleSelection(this.selectedKeys, row);
    },
    async loadRecipients() {
      this.loading = true;
      this.errorText = '';
      try {
        const { data } = await axios.get('/api/sms/past-due');
        this.recipients = Array.isArray(data && data.recipients)
          ? data.recipients
          : [];
        this.selectedKeys = pastDueDefaultSelection(this.recipients);
      } catch (err) {
        this.recipients = [];
        this.selectedKeys = [];
        this.errorText =
          (err.response && err.response.data && err.response.data.error) ||
          'Could not load past due texts.';
      } finally {
        this.loading = false;
      }
    },
    async sendTexts() {
      if (!this.confirmState.canSend || this.sending || this.resultText) return;
      this.sending = true;
      this.errorText = '';
      const chosen = this.recipients.filter((row) => this.isChecked(row));
      try {
        const { data } = await axios.post(
          '/api/sms/past-due',
          pastDueSendBody(chosen),
        );
        this.resultText = pastDueSendResult(data || {});
      } catch (err) {
        this.errorText =
          (err.response && err.response.data && err.response.data.error) ||
          'Could not send past due texts.';
      } finally {
        this.sending = false;
      }
    },
  },
};
</script>

<style scoped>
.past-due-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 4000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.past-due-dialog {
  width: 820px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 32px);
  background: #f8f8f8;
  border: 1px solid #888;
  border-radius: 8px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.past-due-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #6e5875;
  color: #fff;
}

.past-due-header h3 {
  margin: 0;
  font-size: 1.05rem;
}

.past-due-close {
  border: none;
  background: transparent;
  color: #fff;
  cursor: pointer;
  font-size: 1.1rem;
}

.past-due-body {
  padding: 16px;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.past-due-body::-webkit-scrollbar {
  width: 0;
  height: 0;
}

.past-due-hint {
  margin: 0 0 12px;
  font-size: 0.85rem;
  color: #555;
  line-height: 1.4;
}

.past-due-loading {
  text-align: center;
  padding: 24px 0;
  color: #666;
}

.past-due-list {
  margin: 0 0 12px;
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid #ccc;
  background: #fff;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.past-due-list::-webkit-scrollbar {
  width: 0;
  height: 0;
}

.past-due-row {
  display: grid;
  grid-template-columns: 28px minmax(0, 1.4fr) minmax(0, 1.5fr) auto auto;
  gap: 8px;
  align-items: center;
  padding: 8px 10px;
  border-bottom: 1px solid #eee;
  font-size: 0.9rem;
  cursor: pointer;
}

.past-due-head {
  font-weight: 600;
  font-size: 0.85rem;
  cursor: default;
  background: #f3f0f5;
}

.past-due-amount {
  text-align: right;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.past-due-amount-sort {
  border: none;
  background: transparent;
  font: inherit;
  font-weight: 600;
  color: inherit;
  cursor: pointer;
  text-align: right;
  padding: 0;
  justify-self: end;
}

.past-due-optin {
  white-space: nowrap;
  font-size: 0.8rem;
}

.past-due-row.sent {
  color: #888;
}

.past-due-status {
  margin: 12px 0 0;
  min-height: 1.25em;
  font-size: 0.95rem;
  font-weight: 600;
}

.past-due-status.error {
  color: #a33;
}

.past-due-status.success {
  color: #2d6a4f;
}

.past-due-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid #ddd;
}

.past-due-btn {
  padding: 6px 14px;
  cursor: pointer;
}

.past-due-btn.primary {
  background: #6e5875;
  color: #fff;
  border: 1px solid #5a4760;
}

.past-due-btn.secondary {
  background: #eee;
  border: 1px solid #bbb;
}

.past-due-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
